import type { TrackedHistoryRow, TrackedLivePlayer, TrackedTeam } from "../lib/api";
import { relativeTime } from "../lib/format";
import { Link } from "../lib/router";
import { useApp } from "../lib/store";
import { CopyButton, StateChip } from "./bits";

/** One synthesised team: the live match we've found them in + their day so
 *  far. Tracking one member surfaces the whole duo — the members you watch
 *  read in full strength, teammates surfaced from the board read dimmed.
 *  When `windowId` is set the team name links straight into that window. */
export function TeamCard({
  team,
  windowLive,
  windowId,
}: {
  team: TrackedTeam;
  windowLive: boolean;
  windowId?: string;
}) {
  const { unwatchPlayer } = useApp();
  const watchedMembers = team.members.filter(m => m.watched);
  const teammates = team.members.filter(m => !m.watched);
  const following = watchedMembers.length > 0;

  return (
    <article className="fade-up chamfer overflow-hidden bg-surface">
      <header className="flex items-start justify-between gap-3 px-4 pt-3.5">
        <div className="flex min-w-0 items-start gap-2">
          {following && (
            <FollowButton
              label={watchedMembers.map(m => m.display_name).join(" & ")}
              onUnfollow={() => watchedMembers.forEach(m => unwatchPlayer(m.epic_id))}
            />
          )}
          <div className="min-w-0">
            {windowId ? (
              <Link
                to={`/window/${encodeURIComponent(windowId)}`}
                className="min-w-0"
                title="open this window"
              >
                <h3 className="display min-w-0 truncate text-[15px] text-fg transition-colors hover:text-accent-bright">
                  {team.label}
                </h3>
              </Link>
            ) : (
              <h3 className="display min-w-0 truncate text-[15px] text-fg">{team.label}</h3>
            )}
            {teammates.length > 0 && (
              <p className="mt-0.5 truncate font-mono text-[10.5px] text-faint">
                with {teammates.map(m => m.display_name).join(", ")}
              </p>
            )}
          </div>
        </div>
        {team.rank != null ? (
          <span className="shrink-0 whitespace-nowrap font-mono text-[12px] text-dim">
            <span className="text-accent">#{team.rank}</span>
            {team.points != null && <span className="text-faint"> · {team.points} pts</span>}
          </span>
        ) : (
          <span className="shrink-0 whitespace-nowrap font-mono text-[10.5px] uppercase tracking-wider text-faint">
            not on board
          </span>
        )}
      </header>

      <div className="px-4 pb-4 pt-3">
        {team.live ? (
          <LiveMatch team={team} />
        ) : (
          <p className="rounded-md bg-raised/60 px-3 py-2.5 font-mono text-[11px] text-faint">
            {windowLive
              ? "no live match detected yet — scanning lobbies"
              : "not in a match"}
          </p>
        )}

        {team.history.length > 0 && <History rows={team.history} />}
      </div>
    </article>
  );
}

function LiveMatch({ team }: { team: TrackedTeam }) {
  const { isCopied, markCopied, openMatch, admin } = useApp();
  const live = team.live!;
  const isNew = !isCopied(live.session_id);
  // Full elim-by-elim state needs a FULL capture; a roster-only parse
  // (discovery, which runs for every session) also journals watching/done,
  // so gate on the mode too — otherwise a watched-but-not-captured player
  // would falsely read as fully captured.
  const fullyCaptured =
    live.capture_mode === "full" &&
    (live.capture_status === "watching" || live.capture_status === "done");

  const viewLobby = () => {
    openMatch(live.session_id);
    markCopied(live.session_id); // opening the lobby clears the "new" flag
  };

  return (
    <div
      className={`live-glow overflow-hidden rounded-md border ${
        isNew ? "border-accent/60" : "border-accent/30"
      } bg-bg/40`}
    >
      <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
        <span className="flex items-center gap-2">
          <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-accent" />
          <span className="font-mono text-[10.5px] font-medium uppercase tracking-wider text-dim">
            live match
          </span>
          {isNew && (
            <StateChip tone="accent" pulse>
              new
            </StateChip>
          )}
          {admin && (
            <span
              className="font-mono text-[10px] uppercase tracking-wider text-faint"
              title={
                live.source === "epic"
                  ? "Epic's leaderboard carried this live session id"
                  : "found by matching this player against a parsed lobby roster"
              }
            >
              via {live.source}
              {live.source === "epic" && live.roster_confirmed && " ✓"}
            </span>
          )}
        </span>
        {admin && <CaptureChip status={live.capture_status} mode={live.capture_mode} />}
      </div>

      <div className="flex items-center gap-2 px-3 py-2.5">
        <code
          className="min-w-0 flex-1 truncate font-mono text-[12px] text-fg"
          title={live.session_id}
        >
          {live.session_id}
        </code>
        <button
          onClick={viewLobby}
          title="see everyone in this lobby"
          className="press chamfer-sm shrink-0 bg-accent/10 px-2.5 py-1 font-mono text-[10.5px] font-medium uppercase tracking-wider text-accent transition-colors hover:bg-accent/20"
        >
          view lobby →
        </button>
        {/* Not admin-gated: seeing and copying the lobby id to spectate
            in-game is the streamer's core action, so the id and this copy
            button are for everyone, not just admins. */}
        <CopyButton
          text={live.session_id}
          label="copy id"
          onCopy={() => markCopied(live.session_id)}
        />
      </div>

      {live.team ? (
        <div className="grid grid-cols-3 gap-px border-t border-border/60 bg-border/40">
          <LiveStat
            label="projected"
            value={`top ${live.team.projected_placement}`}
            tone="fg"
          />
          <LiveStat label="team elims" value={live.team.elims} tone="fg" />
          <LiveStat label="teams left" value={live.team.teams_remaining} tone="fg" />
        </div>
      ) : (
        !fullyCaptured && (
          <p className="border-t border-border/60 px-3 py-2 font-mono text-[10px] leading-relaxed text-faint">
            live stats appear once this lobby is fully captured
          </p>
        )
      )}

      <ul className="border-t border-border/60">
        {live.players.map(p => (
          <PlayerRow key={p.epic_id} player={p} />
        ))}
      </ul>
    </div>
  );
}

function PlayerRow({ player: p }: { player: TrackedLivePlayer }) {
  // No alive/knocked/eliminated state surfaced: that signal isn't reliable
  // enough from a live replay parse to show honestly. Names + elim counts
  // only; the official result lands later in history.
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-1.5">
      <span className="flex min-w-0 items-center gap-1.5">
        <span
          className={`min-w-0 truncate text-[12.5px] ${
            p.watched ? "font-medium text-fg" : "text-dim"
          }`}
        >
          {p.display_name}
        </span>
        {!p.watched && (
          <span className="shrink-0 font-mono text-[9px] uppercase tracking-wider text-faint">
            teammate
          </span>
        )}
      </span>
      {p.elims > 0 && (
        <span className="shrink-0 font-mono text-[11px] text-dim">
          {p.elims} elim{p.elims === 1 ? "" : "s"}
        </span>
      )}
    </li>
  );
}

function LiveStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone: "fg" | "dim" | "accent";
}) {
  const tones = { fg: "text-fg", dim: "text-dim", accent: "text-accent" };
  return (
    <span className="block bg-surface px-3 py-2">
      <span className={`block font-mono text-[13px] font-semibold ${tones[tone]}`}>{value}</span>
      <span className="label-xs block">{label}</span>
    </span>
  );
}

function History({ rows }: { rows: TrackedHistoryRow[] }) {
  const { openMatch } = useApp();
  return (
    <div className="mt-3">
      <p className="label-xs mb-1.5">Matches today</p>
      <ol className="space-y-1">
        {rows.map((r, i) => (
          <li key={r.session_id}>
            <button
              onClick={() => openMatch(r.session_id)}
              className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-raised"
              title={r.session_id}
            >
              <span className="w-5 shrink-0 text-right font-mono text-[10.5px] text-faint">
                {rows.length - i}
              </span>
              <span
                className={`w-12 shrink-0 font-mono text-[12.5px] font-semibold ${
                  r.placement === 1
                    ? "text-warn"
                    : r.placement <= 10
                      ? "text-fg"
                      : "text-dim"
                }`}
              >
                #{r.placement}
              </span>
              <span className="shrink-0 font-mono text-[11.5px] text-dim">
                {r.team_elims} elim{r.team_elims === 1 ? "" : "s"}
              </span>
              <span className="min-w-0 flex-1 truncate text-right font-mono text-[10.5px] text-faint">
                {r.source === "replay" && (
                  <span
                    className="mr-1.5 text-accent/80"
                    title="replay-derived — official result hasn't landed yet"
                  >
                    replay
                  </span>
                )}
                {r.end_time ? relativeTime(r.end_time) : ""}
              </span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function CaptureChip({ status, mode }: { status: string; mode?: string }) {
  const tone =
    status === "watching"
      ? "accent"
      : status === "done"
        ? "ok"
        : status === "failed"
          ? "err"
          : "dim";
  return (
    <StateChip tone={tone as "accent" | "ok" | "err" | "dim"} pulse={status === "watching"}>
      {status === "watching" ? `parsing${mode === "full" ? " · full" : ""}` : status}
    </StateChip>
  );
}

/** Filled eye = on your dashboard. Click to remove (this browser only).
 *  Clears EVERY watched member of the team, so removing a duo card drops
 *  both — never one half that quietly re-synthesises the card. */
function FollowButton({ label, onUnfollow }: { label: string; onUnfollow: () => void }) {
  const title = `Remove ${label} from your players`;
  return (
    <button
      onClick={onUnfollow}
      title={title}
      aria-label={title}
      className="press -ml-0.5 mt-0.5 shrink-0 rounded p-0.5 text-accent transition-colors hover:text-err"
    >
      <svg
        viewBox="0 0 24 24"
        className="h-3.5 w-3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
        <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />
      </svg>
    </button>
  );
}
